import { Controller } from "@hotwired/stimulus"

// Holt einen AI-Textvorschlag vom Server und füllt Textarea/Felder.
// - Beim ersten Sichtbarwerden des Modals (IntersectionObserver, unabhängig von Bootstrap/jQuery)
//   wird im Hintergrund geladen → "sofort eine KI-Beschreibung". Serverseitig ist das bei
//   vorhandenem Cache billig (kein LLM); nur bei kaltem Cache wird generiert.
// - Der "Erneut generieren"-Button erzwingt eine Neu-Generierung (force).
export default class extends Controller<HTMLElement> {
  static targets = ["textarea"]
  static values = { url: String, kind: String }

  declare readonly textareaTarget: HTMLTextAreaElement
  declare readonly hasTextareaTarget: boolean
  declare urlValue: string
  declare kindValue: string

  private observer?: IntersectionObserver
  private autofetched = false

  connect() {
    this.observer = new IntersectionObserver((entries) => {
      if (!this.autofetched && entries.some((e) => e.isIntersecting)) {
        this.autofetched = true
        this.observer?.disconnect()
        this.fetchSuggestion(false)
      }
    })
    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer?.disconnect()
  }

  // "Erneut generieren"-Button → erzwingt Neu-Generierung
  generate(event: Event) {
    event.preventDefault()
    this.fetchSuggestion(true, event.currentTarget as HTMLButtonElement)
  }

  private async fetchSuggestion(force: boolean, button?: HTMLButtonElement) {
    let original = ""
    if (button) {
      original = button.innerHTML
      button.disabled = true
      button.innerHTML = '<i class="fas fa-spinner fa-spin"></i>'
    }

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: JSON.stringify({ kind: this.kindValue, force }),
      })
      const data = await response.json()
      if (response.ok && data.sections) {
        // Mehrere Felder (z.B. Capterra: titel/text/pro) über data-review-suggestion-field befüllen.
        Object.entries(data.sections).forEach(([key, val]) => {
          const el = this.element.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[data-review-suggestion-field="${key}"]`)
          if (el) {
            el.value = val as string
            el.dispatchEvent(new Event("input"))
          }
        })
      } else if (response.ok && data.text && this.hasTextareaTarget) {
        this.textareaTarget.value = data.text
        this.textareaTarget.dispatchEvent(new Event("input"))
      } else if (button) {
        // Fehler nur bei explizitem Klick zeigen, nicht beim stillen Prefetch.
        window.alert(data.error || "Fehler beim Generieren des Vorschlags.")
      }
    } catch (e) {
      if (button) window.alert("Fehler beim Generieren des Vorschlags.")
    } finally {
      if (button) {
        button.disabled = false
        button.innerHTML = original
      }
    }
  }

  private csrfToken(): string {
    return document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
  }
}
